import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EmbeddedViewRef,
  EnvironmentInjector,
  Injectable,
  Injector,
  OnDestroy,
} from '@angular/core';
import {take} from 'rxjs/operators';
import {
  SharedToastComponent,
} from '../../shared-components/components/shared-toast/shared-toast.component';
import {ISharedToastOptions} from '../../shared-models/toasts/shared-toast';

@Injectable({
  providedIn: 'root',
})
export class SharedToastService implements OnDestroy {

  private activeRef: ComponentRef<SharedToastComponent> | null = null;
  private dismissTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly injector: Injector,
    private readonly appRef: ApplicationRef,
  ) {
  }

  ngOnDestroy(): void {
    this.removeActive();
  }

  /**
   * Presents a toast overlay. A new toast replaces any currently visible toast.
   */
  async present(options: ISharedToastOptions): Promise<void> {
    this.removeActive();

    const toastRef = createComponent(SharedToastComponent, {
      environmentInjector: this.injector as EnvironmentInjector,
    });
    toastRef.setInput('header', options.header ?? '');
    toastRef.setInput('message', options.message);
    toastRef.setInput('colour', options.colour ?? 'primary');
    toastRef.setInput('position', options.position ?? 'bottom');
    toastRef.setInput('buttons', options.buttons ?? []);
    toastRef.setInput('cssClass', options.cssClass);
    toastRef.instance.dismissed.pipe(take(1)).subscribe(() => {
      this.removeActive();
    });

    this.appRef.attachView(toastRef.hostView);
    const hostView = toastRef.hostView as EmbeddedViewRef<unknown>;
    const hostElement = hostView.rootNodes[0] as HTMLElement;
    document.body.appendChild(hostElement);
    toastRef.changeDetectorRef.detectChanges();
    this.activeRef = toastRef;

    const duration = this.resolveDuration(options);
    if (duration > 0) {
      this.dismissTimer = setTimeout(() => {
        this.removeActive();
      }, duration);
    }
  }

  /**
   * Dismisses the currently visible toast, if any.
   */
  async dismiss(): Promise<void> {
    this.removeActive();
  }

  /**
   * Resolves auto-dismiss duration. Persistent when duration is 0 or when action buttons are present.
   */
  private resolveDuration(options: ISharedToastOptions): number {
    if (options.duration !== undefined) {
      return options.duration;
    }
    if (options.buttons && options.buttons.length > 0) {
      return 0;
    }
    return 3000;
  }

  private removeActive(): void {
    if (this.dismissTimer !== null) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
    if (!this.activeRef) {
      return;
    }
    this.appRef.detachView(this.activeRef.hostView);
    this.activeRef.destroy();
    this.activeRef = null;
  }
}
